import React, { memo, useMemo } from 'react';
import { Pawn } from './Pawn.jsx';
import { Wall } from './Wall.jsx';

const CELL = 8;    // cell track weight
const GROOVE = 1.9; // wall-slot track weight (keeps walls thick)

function tracks(n) {
  const parts = [];
  for (let i = 0; i < n; i++) {
    parts.push(`${CELL}fr`);
    if (i < n - 1) parts.push(`${GROOVE}fr`);
  }
  return parts.join(' ');
}

/** Percent offset of a cell center within the grid (matches CELL/GROOVE tracks). */
function cellCenterPct(index, n) {
  const total = n * CELL + (n - 1) * GROOVE;
  const start = index * (CELL + GROOVE);
  return ((start + CELL / 2) / total) * 100;
}

/**
 * Board — a minimal Quoridor field: a thin hairline lattice on paper,
 * with thick contrasting walls, pawns, legal-move dots and a wall ghost.
 */
function BoardImpl({
  size = 9,
  pawns = [],
  walls = [],
  highlights = [],
  preview = null,
  onCellClick,
  onWallClick,
  innerRef = null,
  style = {},
}) {
  const tpl = useMemo(() => tracks(size), [size]);
  const hlSet = useMemo(
    () => new Set(highlights.map((h) => `${h.row},${h.col}`)),
    [highlights],
  );

  // Cells — plain paper; goal rows get a faint clay wash; highlights get a dot
  const cells = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const goal = r === 0 || r === size - 1;
      const isHl = hlSet.has(`${r},${c}`);
      cells.push(
        <div
          key={`c-${r}-${c}`}
          role="gridcell"
          aria-label={`Клетка ${r + 1}, ${c + 1}${isHl ? ', доступный ход' : ''}`}
          tabIndex={isHl && onCellClick ? 0 : -1}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && isHl && onCellClick) {
              e.preventDefault();
              onCellClick(r, c);
            }
          }}
          onClick={() => onCellClick && onCellClick(r, c)}
          style={{
            gridColumn: 2 * c + 1, gridRow: 2 * r + 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: goal ? 'var(--board-target)' : 'transparent',
            borderRadius: 'var(--radius-xs)',
            cursor: (onCellClick && isHl) ? 'pointer' : 'default',
            position: 'relative',
          }}
        >
          {isHl && (
            <span style={{
              width: '28%', aspectRatio: '1/1', borderRadius: '50%',
              background: 'var(--accent)', opacity: 0.6,
              animation: 'kor-pulse 1.6s var(--ease-standard) infinite',
            }} />
          )}
        </div>
      );
    }
  }

  // Thin hairline lattice — the whole "field" is just these lines
  const lines = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size - 1; c++) {
      lines.push(<div key={`lv-${r}-${c}`} style={{ gridColumn: 2 * c + 2, gridRow: 2 * r + 1, width: 1.5, height: '92%', justifySelf: 'center', alignSelf: 'center', background: 'var(--board-groove)', borderRadius: 2 }} />);
    }
  }
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size; c++) {
      lines.push(<div key={`lh-${r}-${c}`} style={{ gridColumn: 2 * c + 1, gridRow: 2 * r + 2, height: 1.5, width: '92%', justifySelf: 'center', alignSelf: 'center', background: 'var(--board-groove)', borderRadius: 2 }} />);
    }
  }

  // Absolute pawns so left/top can transition between cells.
  const pawnEls = pawns.map((p) => {
    const left = cellCenterPct(p.col, size);
    const top = cellCenterPct(p.row, size);
    return (
      <div
        key={`p-${p.player}`}
        style={{
          position: 'absolute',
          left: `${left}%`,
          top: `${top}%`,
          width: `${(CELL / (size * CELL + (size - 1) * GROOVE)) * 100}%`,
          aspectRatio: '1 / 1',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3,
          pointerEvents: 'none',
          transition: 'left 220ms var(--ease-spring), top 220ms var(--ease-spring)',
          willChange: 'left, top',
        }}
      >
        <Pawn player={p.player} active={p.active} />
      </div>
    );
  });

  const wallEls = walls.map((w) => {
    const h = w.orientation === 'h';
    return (
      <div
        key={`w-${w.row}-${w.col}-${w.orientation}`}
        style={{
          gridColumn: h ? `${2 * w.col + 1} / span 3` : 2 * w.col + 2,
          gridRow: h ? 2 * w.row + 2 : `${2 * w.row + 1} / span 3`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4,
          animation: 'kor-wall-in 180ms var(--ease-out)',
        }}
      >
        <Wall orientation={w.orientation} />
      </div>
    );
  });

  let previewEl = null;
  if (preview) {
    const h = preview.orientation === 'h';
    previewEl = (
      <div
        onClick={() => onWallClick && onWallClick(preview.row, preview.col, preview.orientation)}
        style={{
          gridColumn: h ? `${2 * preview.col + 1} / span 3` : 2 * preview.col + 2,
          gridRow: h ? 2 * preview.row + 2 : `${2 * preview.row + 1} / span 3`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5,
          cursor: 'pointer',
        }}
      >
        <Wall orientation={preview.orientation} ghost />
      </div>
    );
  }

  return (
    <div style={{
      width: '100%', aspectRatio: '1 / 1', padding: '3%',
      background: 'var(--board-bg)',
      border: '1px solid var(--board-groove)',
      borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)',
      boxSizing: 'border-box', ...style,
    }}>
      <div ref={innerRef} role="grid" aria-label={`Игровое поле ${size} на ${size}`} style={{
        display: 'grid', width: '100%', height: '100%',
        gridTemplateColumns: tpl, gridTemplateRows: tpl, gap: 0,
        position: 'relative',
      }}>
        {cells}
        {lines}
        {wallEls}
        {previewEl}
        {pawnEls}
      </div>
    </div>
  );
}

function highlightsEqual(a = [], b = []) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].row !== b[i].row || a[i].col !== b[i].col) return false;
  }
  return true;
}

function pawnsEqual(a = [], b = []) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].row !== b[i].row
      || a[i].col !== b[i].col
      || a[i].player !== b[i].player
      || a[i].active !== b[i].active
    ) return false;
  }
  return true;
}

function wallsEqual(a = [], b = []) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].row !== b[i].row
      || a[i].col !== b[i].col
      || a[i].orientation !== b[i].orientation
    ) return false;
  }
  return true;
}

function previewEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.row === b.row && a.col === b.col && a.orientation === b.orientation;
}

export const Board = memo(BoardImpl, (prev, next) => (
  prev.size === next.size
  && prev.onCellClick === next.onCellClick
  && prev.onWallClick === next.onWallClick
  && prev.innerRef === next.innerRef
  && highlightsEqual(prev.highlights, next.highlights)
  && pawnsEqual(prev.pawns, next.pawns)
  && wallsEqual(prev.walls, next.walls)
  && previewEqual(prev.preview, next.preview)
));
